import type { SiteResource } from '@/types/site';
import { createSiteResourceCode } from '@/lib/siteResource';
import { translateApiMessage } from '@/lib/utils';

export type ResourceType = 'asset' | 'business' | 'content' | 'template';
export type AssetTransportKind = 'image' | 'pdf' | 'file' | 'url';
export type ResourceFilter = 'all' | 'image' | 'pdf' | 'url';
export type AssetFilterKind = Exclude<ResourceFilter, 'all'>;

export type ResourceMeta = {
  label: string;
  icon: string;
};

export type AssetEditorKind = 'image' | 'pdf' | 'url';

export type ResourceSubtypeOption = {
  value: string;
  label: string;
};

export type StructuredFieldSpec = {
  id: string;
  key: keyof ResourceDraft;
  label: string;
  placeholder: string;
  value: string;
  kind?: 'input' | 'textarea';
  type?: 'text' | 'email' | 'tel';
  inputMode?: 'text' | 'email' | 'tel';
  autoComplete?: string;
  rows?: number;
  wide?: boolean;
};

export type ResourceDraft = {
  id?: number;
  resourceCode: string;
  resourceType: ResourceType;
  subtype: string;
  title: string;
  locator: string;
  note: string;
  phone: string;
  email: string;
  address: string;
  question: string;
  answer: string;
  body: string;
  description: string;
  target: string;
  mimeType?: string;
  fileSize?: number;
  coverUrl?: string;
  objectName?: string;
  metaJson?: string;
  sourceType?: string;
  storageType?: string;
  contentSchema?: string;
};

export type UpdateDraft = (next: Partial<ResourceDraft>) => void;

export const RESOURCE_TYPE_ORDER: ResourceType[] = ['asset', 'business', 'content', 'template'];

export const RESOURCE_TYPE_META: Record<ResourceType, ResourceMeta> = {
  asset: { label: 'Asset', icon: 'fa-photo-film' },
  business: { label: 'Business', icon: 'fa-address-card' },
  content: { label: 'Content', icon: 'fa-pen-ruler' },
  template: { label: 'Template', icon: 'fa-layer-group' },
};

export const ASSET_EDITOR_KIND_META: Record<AssetEditorKind, ResourceMeta> = {
  image: { label: 'Image', icon: 'fa-image' },
  pdf: { label: 'PDF', icon: 'fa-file-pdf' },
  url: { label: 'URL', icon: 'fa-link' },
};

export const ASSET_EDITOR_KIND_ORDER: AssetEditorKind[] = ['image', 'pdf', 'url'];

export const RESOURCE_SUBTYPE_OPTIONS: Record<ResourceType, ResourceSubtypeOption[]> = {
  asset: [
    { value: 'logo', label: 'Logo' },
    { value: 'other-image', label: 'Other image' },
    { value: 'pdf', label: 'PDF' },
    { value: 'official-website', label: 'Official website' },
    { value: 'internal-link', label: 'Internal link' },
    { value: 'reference-website', label: 'Reference website' },
    { value: 'file', label: 'File' },
  ],
  business: [
    { value: 'contact', label: 'Contact' },
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'address', label: 'Address' },
  ],
  content: [
    { value: 'copy', label: 'Copy' },
    { value: 'faq', label: 'FAQ' },
    { value: 'offer', label: 'Offer' },
  ],
  template: [
    { value: 'page', label: 'Page template' },
    { value: 'section', label: 'Section template' },
    { value: 'prompt', label: 'Prompt template' },
  ],
};

const URL_ASSET_SUBTYPES = new Set([
  'url',
  'official-website',
  'internal-link',
  'reference-website',
]);

export const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? translateApiMessage(error.message) : fallback;

export const normalizeSubtype = (value?: string) =>
  String(value || '')
    .trim()
    .toLowerCase();

export const normalizeResourceType = (value?: string): ResourceType => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'business') return 'business';
  if (normalized === 'content') return 'content';
  if (normalized === 'template') return 'template';
  return 'asset';
};

export const humanizeSubtype = (value: string) =>
  value
    .split(/[-_.]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ') || 'General';

export const ensureSubtypeOptions = (resourceType: ResourceType, subtype?: string) => {
  const options = RESOURCE_SUBTYPE_OPTIONS[resourceType];
  const normalizedSubtype = normalizeSubtype(subtype);
  if (!normalizedSubtype || options.some((option) => option.value === normalizedSubtype)) {
    return options;
  }

  return [
    ...options,
    {
      value: normalizedSubtype,
      label: humanizeSubtype(normalizedSubtype),
    },
  ];
};

export const getSubtypeLabel = (resourceType: ResourceType, subtype: string) =>
  ensureSubtypeOptions(resourceType, subtype).find(
    (option) => option.value === normalizeSubtype(subtype),
  )?.label || humanizeSubtype(subtype);

export const getAssetTransportKind = (
  subtype?: string,
  mimeType?: string,
  locator?: string,
): AssetTransportKind => {
  const normalizedSubtype = normalizeSubtype(subtype);
  const normalizedMimeType = String(mimeType || '')
    .trim()
    .toLowerCase();
  const normalizedLocator = String(locator || '')
    .trim()
    .toLowerCase();

  if (
    normalizedSubtype === 'pdf' ||
    normalizedMimeType === 'application/pdf' ||
    normalizedLocator.endsWith('.pdf')
  ) {
    return 'pdf';
  }
  if (normalizedSubtype === 'file') return 'file';
  if (URL_ASSET_SUBTYPES.has(normalizedSubtype)) return 'url';
  return 'image';
};

export const getResourceFilterKind = (resource: SiteResource): AssetFilterKind | null => {
  if (normalizeResourceType(resource.resourceType) !== 'asset') return null;
  const assetKind = getAssetTransportKind(
    resource.resourceSubtype,
    resource.mimeType,
    resource.primaryUrl,
  );
  return assetKind === 'file' ? 'pdf' : assetKind;
};

export const getDraftFilterKind = (
  draft: Pick<ResourceDraft, 'resourceType' | 'subtype' | 'mimeType' | 'locator'>,
): ResourceFilter => {
  if (draft.resourceType !== 'asset') return 'all';
  const assetKind = getAssetTransportKind(draft.subtype, draft.mimeType, draft.locator);
  return assetKind === 'file' ? 'pdf' : assetKind;
};

export const getDefaultSubtype = (resourceType: ResourceType) =>
  RESOURCE_SUBTYPE_OPTIONS[resourceType][0].value;

export const getAssetEditorKind = (
  draft: Pick<ResourceDraft, 'subtype' | 'mimeType' | 'locator'>,
): AssetEditorKind => {
  const assetKind = getAssetTransportKind(draft.subtype, draft.mimeType, draft.locator);
  return assetKind === 'file' ? 'pdf' : assetKind;
};

export const getDefaultAssetSubtype = (kind: AssetEditorKind) =>
  kind === 'image' ? 'logo' : kind === 'pdf' ? 'pdf' : 'official-website';

export const getAssetEditorSubtypeOptions = (kind: AssetEditorKind): ResourceSubtypeOption[] =>
  kind === 'image'
    ? [
        { value: 'logo', label: 'Logo' },
        { value: 'other-image', label: 'Other image' },
      ]
    : kind === 'pdf'
      ? [{ value: 'pdf', label: 'PDF' }]
      : [
          { value: 'official-website', label: 'Official website' },
          { value: 'internal-link', label: 'Internal link' },
          { value: 'reference-website', label: 'Reference website' },
        ];

export const getAssetEditorSubtypeValue = (
  draft: Pick<ResourceDraft, 'subtype' | 'mimeType' | 'locator'>,
) => {
  const kind = getAssetEditorKind(draft);
  const normalizedSubtype = normalizeSubtype(draft.subtype);
  if (kind === 'image') {
    return normalizedSubtype === 'logo' ? 'logo' : 'other-image';
  }
  if (kind === 'pdf') return 'pdf';
  return ['official-website', 'internal-link', 'reference-website'].includes(normalizedSubtype)
    ? normalizedSubtype
    : 'official-website';
};

export const getAssetEditorMeta = (
  draft: Pick<ResourceDraft, 'subtype' | 'mimeType' | 'locator'>,
) => ASSET_EDITOR_KIND_META[getAssetEditorKind(draft)];

export const normalizeDraftToAssetEditor = (draft: ResourceDraft): ResourceDraft => {
  const subtype = getAssetEditorSubtypeValue(draft);
  const isExternalUrl = URL_ASSET_SUBTYPES.has(normalizeSubtype(subtype));

  return {
    ...draft,
    resourceType: 'asset',
    subtype,
    target: isExternalUrl ? draft.target.trim() || '_blank' : '',
    storageType: isExternalUrl ? draft.storageType || 'external' : draft.storageType || 'inline',
    contentSchema: getDraftSchemaName('asset', subtype, draft.mimeType, draft.locator),
  };
};

export const getResourceTypeFieldLabel = () => 'Resource type';

export const getResourceSubtypeFieldLabel = (resourceType: ResourceType) =>
  resourceType === 'asset' ? 'Category' : 'Resource subtype';

export const getResourceTitleFieldLabel = (resourceType: ResourceType) =>
  resourceType === 'asset' ? 'Resource name' : 'Display name';

export const getResourceTitlePlaceholder = (resourceType: ResourceType) =>
  resourceType === 'asset'
    ? 'Primary policy guide'
    : 'Primary contact / Hero image / Landing page template';

export const resetDraftFieldsForResourceShape = (
  draft: ResourceDraft,
  nextResourceType: ResourceType,
  nextSubtype: string,
): ResourceDraft => ({
  ...draft,
  resourceType: nextResourceType,
  subtype: nextSubtype,
  title: '',
  locator: '',
  note: '',
  phone: '',
  email: '',
  address: '',
  question: '',
  answer: '',
  body: '',
  description: '',
  mimeType: '',
  fileSize: undefined,
  coverUrl: '',
  objectName: undefined,
  target:
    nextResourceType === 'asset' && URL_ASSET_SUBTYPES.has(normalizeSubtype(nextSubtype))
      ? '_blank'
      : '',
  metaJson: draft.metaJson,
  sourceType: 'manual',
  storageType:
    nextResourceType === 'asset' && URL_ASSET_SUBTYPES.has(normalizeSubtype(nextSubtype))
      ? 'external'
      : nextResourceType === 'asset'
        ? 'inline'
        : 'inline',
  contentSchema: getDraftSchemaName(nextResourceType, nextSubtype, draft.mimeType, ''),
  resourceCode: createSiteResourceCode(nextResourceType, nextSubtype),
});

export const getDraftSchemaName = (
  resourceType: ResourceType,
  subtype: string,
  mimeType?: string,
  locator?: string,
) => {
  if (resourceType === 'template') return `template.${normalizeSubtype(subtype) || 'page'}`;
  if (resourceType === 'business') return `business.${normalizeSubtype(subtype) || 'contact'}`;
  if (resourceType === 'content') return `content.${normalizeSubtype(subtype) || 'copy'}`;

  const assetKind = getAssetTransportKind(subtype, mimeType, locator);
  if (assetKind === 'url') return 'asset.url';
  if (assetKind === 'pdf') return 'asset.pdf';
  if (assetKind === 'file') return 'asset.file';
  return 'asset.image';
};

export const buildDraftResourceCode = (
  draft: Pick<ResourceDraft, 'resourceCode' | 'resourceType' | 'subtype'>,
) => draft.resourceCode || createSiteResourceCode(draft.resourceType, draft.subtype);

export const truncateText = (value: string | undefined, maxLength = 120) => {
  const normalized = String(value || '').trim();
  if (!normalized || normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(maxLength - 3, 0))}...`;
};

export const getDraftDerivedTitle = (draft: ResourceDraft) => {
  const title = draft.title.trim();
  if (title) return title;

  if (draft.resourceType === 'business') {
    if (draft.subtype === 'phone') return draft.phone.trim();
    if (draft.subtype === 'email') return draft.email.trim();
    if (draft.subtype === 'address') return draft.address.trim();
    return [draft.phone.trim(), draft.email.trim(), draft.address.trim()].find(Boolean) || '';
  }

  if (draft.resourceType === 'content') {
    if (draft.subtype === 'faq') return draft.question.trim();
    if (draft.subtype === 'copy') return draft.description.trim() || draft.body.trim().slice(0, 48);
    if (draft.subtype === 'offer') return draft.description.trim().slice(0, 48);
  }

  if (draft.resourceType === 'template') {
    return draft.body.trim().slice(0, 48);
  }

  return draft.locator.trim();
};

export const inferStorageType = (draft: ResourceDraft) => {
  if (draft.resourceType === 'asset') {
    const assetKind = getAssetTransportKind(draft.subtype, draft.mimeType, draft.locator);
    if (assetKind === 'url') return 'external';
    if (draft.objectName) return 'oss';
    return 'inline';
  }

  if (draft.resourceType === 'content' && draft.subtype === 'offer' && draft.locator.trim()) {
    return 'external';
  }

  return 'inline';
};

export const getDraftPrimaryText = (draft: ResourceDraft) => getDraftDerivedTitle(draft) || '';

export const isValidResourcePhone = (value: string) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  const normalized = trimmed.replace(/[\s\-().]/g, '');
  return /^\+?\d{6,20}$/.test(normalized);
};

export const isValidResourceEmail = (value: string) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

export const getDraftFieldError = (
  draft: Pick<ResourceDraft, 'resourceType' | 'subtype' | 'phone' | 'email'>,
  fieldKey: 'phone' | 'email',
) => {
  if (draft.resourceType !== 'business') return '';

  if (fieldKey === 'phone') {
    const value = draft.phone.trim();
    const shouldValidate = draft.subtype === 'phone' || draft.subtype === 'contact';
    if (!shouldValidate || !value) return '';
    return isValidResourcePhone(value)
      ? ''
      : 'Enter a valid phone number using digits, spaces, or + - ( ).';
  }

  const value = draft.email.trim();
  const shouldValidate = draft.subtype === 'email' || draft.subtype === 'contact';
  if (!shouldValidate || !value) return '';
  return isValidResourceEmail(value) ? '' : 'Enter a valid email address.';
};

export const getDraftValidationError = (draft: ResourceDraft) => {
  if (draft.resourceType === 'asset' && !draft.locator.trim()) {
    return 'Add a file path or URL before saving';
  }

  if (draft.resourceType === 'business') {
    if (
      draft.subtype === 'contact' &&
      !draft.phone.trim() &&
      !draft.email.trim() &&
      !draft.address.trim()
    ) {
      return 'Add at least one contact field before saving';
    }
    if (draft.subtype === 'phone' && !draft.phone.trim()) return 'Add a phone number before saving';
    if (draft.subtype === 'email' && !draft.email.trim()) {
      return 'Add an email address before saving';
    }
    if (draft.subtype === 'address' && !draft.address.trim()) {
      return 'Add an address before saving';
    }
    if (draft.phone.trim() && getDraftFieldError(draft, 'phone')) {
      return 'Enter a valid phone number before saving';
    }
    if (draft.email.trim() && getDraftFieldError(draft, 'email')) {
      return 'Enter a valid email address before saving';
    }
  }

  if (draft.resourceType === 'content') {
    if (draft.subtype === 'faq' && (!draft.question.trim() || !draft.answer.trim())) {
      return 'Add both question and answer before saving';
    }
    if (draft.subtype === 'copy' && !draft.body.trim()) return 'Add copy content before saving';
    if (draft.subtype === 'offer' && !draft.description.trim()) {
      return 'Add offer details before saving';
    }
  }

  if (draft.resourceType === 'template' && !draft.body.trim()) {
    return 'Add template content before saving';
  }

  return '';
};

export const getLocatorConfig = (draft: ResourceDraft) => {
  if (draft.resourceType === 'asset') {
    const assetKind = getAssetTransportKind(draft.subtype, draft.mimeType, draft.locator);
    if (assetKind === 'pdf') {
      return {
        hint: 'Use a file path, CDN path, or full URL so the team knows exactly which resource Autopilot should reference.',
        label: 'PDF path or URL',
        placeholder: 'https://docs.example.com/resource.pdf',
        required: true,
      };
    }
    if (assetKind === 'file') {
      return {
        hint: 'Use the final file URL or upload a replacement file.',
        label: 'File path or URL',
        placeholder: 'https://cdn.example.com/files/guide.zip',
        required: true,
      };
    }
    if (assetKind === 'url') {
      return {
        hint: 'Use a file path, CDN path, or full URL so the team knows exactly which resource Autopilot should reference.',
        label: 'Resource URL',
        placeholder: 'https://example.com/resource',
        required: true,
      };
    }
    return {
      hint: 'Use a file path, CDN path, or full URL so the team knows exactly which resource Autopilot should reference.',
      label: 'Image file or CDN path',
      placeholder: 'assets/brand/hero.webp',
      required: true,
    };
  }

  if (draft.resourceType === 'content' && draft.subtype === 'offer') {
    return {
      hint: 'Optional link Autopilot can use as the offer destination.',
      label: 'Offer URL',
      placeholder: 'https://example.com/promo',
      required: false,
    };
  }

  if (draft.resourceType === 'template') {
    return {
      hint: 'Optional reference URL or preview path for this reusable template.',
      label: 'Reference URL',
      placeholder: 'https://example.com/templates/landing-page',
      required: false,
    };
  }

  return null;
};

export const getStructuredFieldSpecs = (draft: ResourceDraft): StructuredFieldSpec[] => {
  const input = (
    id: string,
    label: string,
    value: string,
    placeholder: string,
  ): StructuredFieldSpec => ({
    id,
    key:
      id === 'site-resource-phone' || id === 'site-resource-contact-phone'
        ? 'phone'
        : id === 'site-resource-email' || id === 'site-resource-contact-email'
          ? 'email'
          : id === 'site-resource-question'
            ? 'question'
            : id === 'site-resource-answer'
              ? 'answer'
              : id === 'site-resource-offer'
                ? 'description'
                : id === 'site-resource-copy-summary'
                  ? 'description'
                  : id === 'site-resource-target'
                    ? 'target'
                    : 'title',
    label,
    placeholder,
    value,
    type:
      id === 'site-resource-phone' || id === 'site-resource-contact-phone'
        ? 'tel'
        : id === 'site-resource-email' || id === 'site-resource-contact-email'
          ? 'email'
          : 'text',
    inputMode:
      id === 'site-resource-phone' || id === 'site-resource-contact-phone'
        ? 'tel'
        : id === 'site-resource-email' || id === 'site-resource-contact-email'
          ? 'email'
          : 'text',
    autoComplete:
      id === 'site-resource-phone' || id === 'site-resource-contact-phone'
        ? 'tel'
        : id === 'site-resource-email' || id === 'site-resource-contact-email'
          ? 'email'
          : 'off',
  });
  const textarea = (
    id: string,
    label: string,
    value: string,
    placeholder: string,
    rows = 3,
    wide = false,
  ): StructuredFieldSpec => ({
    id,
    key:
      id === 'site-resource-address' || id === 'site-resource-contact-address'
        ? 'address'
        : id === 'site-resource-answer'
          ? 'answer'
          : id === 'site-resource-offer'
            ? 'description'
            : id === 'site-resource-copy'
              ? 'body'
              : id === 'site-resource-template-body'
                ? 'body'
                : 'body',
    label,
    placeholder,
    value,
    kind: 'textarea',
    rows,
    wide,
  });

  if (draft.resourceType === 'business') {
    if (draft.subtype === 'phone') {
      return [input('site-resource-phone', 'Phone number', draft.phone, '+86 400-000-0000')];
    }
    if (draft.subtype === 'email') {
      return [input('site-resource-email', 'Email address', draft.email, 'hi@example.com')];
    }
    if (draft.subtype === 'address') {
      return [
        textarea(
          'site-resource-address',
          'Address',
          draft.address,
          'Shanghai, Pudong New Area...',
          3,
        ),
      ];
    }
    return [
      input('site-resource-contact-phone', 'Phone number', draft.phone, '+86 400-000-0000'),
      input('site-resource-contact-email', 'Email address', draft.email, 'hi@example.com'),
      textarea(
        'site-resource-contact-address',
        'Address',
        draft.address,
        'Shanghai, Pudong New Area...',
        3,
        true,
      ),
    ];
  }

  if (draft.resourceType === 'content') {
    if (draft.subtype === 'faq') {
      return [
        input('site-resource-question', 'Question', draft.question, 'What is your return policy?'),
        textarea(
          'site-resource-answer',
          'Answer',
          draft.answer,
          'We accept returns within 30 days...',
          4,
        ),
      ];
    }
    if (draft.subtype === 'offer') {
      return [
        textarea(
          'site-resource-offer',
          'Offer details',
          draft.description,
          'First order discount, free consultation, seasonal promotion...',
          4,
        ),
      ];
    }
    return [
      textarea(
        'site-resource-copy',
        'Copy body',
        draft.body,
        'Add the reusable body copy here...',
        5,
      ),
      input(
        'site-resource-copy-summary',
        'Summary',
        draft.description,
        'Short summary for quick reuse',
      ),
    ];
  }

  if (draft.resourceType === 'template') {
    return [
      textarea(
        'site-resource-template-body',
        'Template body',
        draft.body,
        'Add the reusable template body, prompt, or markup...',
        6,
      ),
    ];
  }

  return [];
};
